import { Role } from "src/auth/types/interfaces";

import type { LobKey } from "./types";

/**
 * Mapping of each LOB to the Cognito roles that grant access to its
 * pre-screen flow. A user has access to an LOB if they hold at least one
 * of the listed roles.
 */
export const LOB_ROLES: Record<LobKey, Role[]> = {
  wc: [
    Role.WC_CLAIM_USER,
    Role.WC_CLAIM_USER_LIMITED,
    Role.WC_CLAIM_CANADA_USER,
    Role.WC_CLAIM_PRODUCT_GROUP,
  ],
  cargo: [Role.CARGO_CLAIM_USER, Role.WC_CLAIM_PRODUCT_GROUP],
  ipl: [Role.IPL_CLAIM_USER, Role.WC_CLAIM_PRODUCT_GROUP],
  atoz: [Role.ATOZ_CLAIM_USER, Role.WC_CLAIM_PRODUCT_GROUP],
};

/**
 * Display order for LOBs in the LOB selector. The JsonFormsClaimIntakeApp renders
 * available LOBs in this fixed order so users always see the same layout.
 */
export const LOB_ORDER: LobKey[] = ["wc", "cargo", "ipl", "atoz"];

/**
 * Per-LOB display label rendered as the LOB selector card header.
 *
 * Lives next to `LOB_ORDER` so adding a new LOB is one edit per file:
 * one entry here, one entry in `LOB_DESCRIPTIONS`, one entry in
 * `LOB_ROLES`, one entry in `LOB_ORDER`.
 */
export const LOB_LABELS: Record<LobKey, string> = {
  wc: "Workers Compensation",
  cargo: "Cargo",
  ipl: "International Product Liability",
  atoz: "AtoZ",
};

/**
 * Per-LOB description rendered inside each LOB selector card body.
 */
export const LOB_DESCRIPTIONS: Record<LobKey, string> = {
  wc: "Submit a Workers Compensation claim for an associate injury or workplace incident.",
  cargo: "Submit a Cargo claim for shipping or transportation damage and loss.",
  ipl: "Submit an International Product Liability claim for products sold by Amazon.",
  atoz: "Submit an AtoZ claim.",
};

/**
 * Compute the set of LOBs available to a user given their roles.
 *
 * Returns LOBs in `LOB_ORDER` so callers can render a deterministic list.
 *
 * @param roles - The roles assigned to the current user.
 * @returns The LOBs the user has access to, in display order.
 */
export function getAvailableLobsForRoles(roles: Role[]): LobKey[] {
  return LOB_ORDER.filter((lob) => LOB_ROLES[lob].some((role) => roles.includes(role)));
}

/**
 * Determine whether a user has access to a specific LOB.
 *
 * @param roles - The roles assigned to the current user.
 * @param lob - The LOB to check.
 * @returns `true` if the user has at least one role in `LOB_ROLES[lob]`.
 */
export function userHasLobAccess(roles: Role[], lob: LobKey): boolean {
  return LOB_ROLES[lob].some((role) => roles.includes(role));
}

/**
 * Normalize a raw `<lob>` URL path parameter to a `LobKey`.
 *
 * Matching is case-insensitive. Returns `null` if the input is missing or
 * does not match any known LOB key, allowing the shell to render an
 * access-denied alert for unknown values.
 *
 * @param raw - The raw URL parameter as provided by `useParams`.
 * @returns The corresponding `LobKey`, or `null` if the value is unknown.
 */
export function normalizeLobParam(raw?: string): LobKey | null {
  if (!raw) {
    return null;
  }
  const lower = raw.toLowerCase();
  return (LOB_ORDER as string[]).includes(lower) ? (lower as LobKey) : null;
}
